import { TutorEntry } from "../game/controller";
import { Play } from "../engine/moves";
import { OFF, BAR } from "../engine/position";

export function renderTutorCard(parent: HTMLElement, entry: TutorEntry | undefined): void {
  parent.innerHTML = "";
  // No card for good moves — the absence of a flag is itself the feedback.
  if (!entry || entry.classification === "good") return;
  const chip = document.createElement("div");
  chip.className = `tutor-chip ${entry.classification}`;
  chip.textContent = initialFor(entry.classification);
  chip.title = `${labelFor(entry.classification, entry.equityLoss)} — Best: ${describePlay(entry.bestPlay)}`;
  parent.appendChild(chip);
}

function initialFor(c: TutorEntry["classification"]): string {
  switch (c) {
    case "good":
      return "G";
    case "doubtful":
      return "?";
    case "error":
      return "!";
    case "blunder":
      return "‼";
  }
}

function labelFor(c: TutorEntry["classification"], loss: number): string {
  const lossStr = loss < 0.001 ? "" : ` · ${loss.toFixed(3)}`;
  switch (c) {
    case "good":
      return `Good${lossStr}`;
    case "doubtful":
      return `Doubtful${lossStr}`;
    case "error":
      return `Error${lossStr}`;
    case "blunder":
      return `Blunder${lossStr}`;
  }
}

export function describePlay(play: Play): string {
  if (play.length === 0) return "no move";
  return play.map(describeSub).join(", ");
}

function describeSub(sub: { from: number; to: number; die: number }): string {
  const from = sub.from === BAR ? "bar" : String(sub.from + 1);
  const to = sub.to === OFF ? "off" : String(sub.to + 1);
  return `${from}/${to}`;
}

interface SideTotals {
  moves: number;
  /** Turns that offered a real choice — the denominator PR is measured over. */
  decisions: number;
  loss: number;
  counts: Record<string, number>;
}

function emptyTotals(): SideTotals {
  return { moves: 0, decisions: 0, loss: 0, counts: { good: 0, doubtful: 0, error: 0, blunder: 0 } };
}

/**
 * Performance rating: average equity given up per *decision*, scaled by 500 —
 * the same convention gnubg and XG use, so the tier names below mean what they
 * mean everywhere else. 0 is flawless play; there is no upper bound.
 *
 * Turns with no real choice are excluded. They can only ever score a 0 loss,
 * and measured over full games roughly one turn in eight is forced, so
 * counting them was quietly scaling every rating down toward "World class"
 * regardless of how the player actually played.
 */
function performanceRating(t: { decisions: number; loss: number }): number | null {
  if (t.decisions <= 0) return null;
  return (t.loss / t.decisions) * 500;
}

export function renderPostGameReport(parent: HTMLElement, history: TutorEntry[], whiteName: string, blackName: string): void {
  parent.innerHTML = "";
  const wrap = document.createElement("div");
  wrap.className = "report";
  const totals: Record<0 | 1, SideTotals> = { 0: emptyTotals(), 1: emptyTotals() };
  for (const e of history) {
    const t = totals[e.side];
    t.moves++;
    // A turn with no choice isn't a good move, it's a non-move — keep it out
    // of the breakdown as well as out of the rating.
    if (e.forced) continue;
    t.decisions++;
    t.loss += e.equityLoss;
    t.counts[e.classification]++;
  }
  for (const side of [0, 1] as const) {
    const t = totals[side];
    if (t.moves === 0) continue;
    const name = side === 0 ? whiteName : blackName;
    const pr = performanceRating(t);
    const h = document.createElement("h3");
    h.textContent = pr === null ? `${name} — no rated decisions yet` : `${name} — PR ${pr.toFixed(1)} (${prTier(pr)})`;
    wrap.appendChild(h);
    const tbl = document.createElement("table");
    tbl.innerHTML = `
      <tbody>
        <tr><td>Decisions rated</td><td>${t.decisions} of ${t.moves} turns</td></tr>
        <tr><td>Total equity lost</td><td>${t.loss.toFixed(3)}</td></tr>
        <tr><td>Good</td><td>${t.counts.good}</td></tr>
        <tr><td>Doubtful</td><td>${t.counts.doubtful}</td></tr>
        <tr><td>Errors</td><td>${t.counts.error}</td></tr>
        <tr><td>Blunders</td><td>${t.counts.blunder}</td></tr>
      </tbody>
    `;
    wrap.appendChild(tbl);
  }
  // The number is meaningless without its definition, and "PR" is jargon.
  const note = document.createElement("p");
  note.className = "report-note";
  note.textContent =
    "PR (performance rating) is the average equity you gave up per decision, ×500. "
    + "0 is perfect play; lower is better. Turns with only one legal move don't count.";
  wrap.appendChild(note);
  parent.appendChild(wrap);
}

// Standard backgammon performance rating tiers (XG / Backgammon Galaxy).
// PR is millipoints-per-decision equity loss — lower is better.
function prTier(pr: number): string {
  if (!Number.isFinite(pr)) return "unrated";
  if (pr < 2) return "World class";
  if (pr < 4) return "Expert";
  if (pr < 7) return "Advanced";
  if (pr < 12) return "Intermediate";
  if (pr < 20) return "Casual";
  return "Beginner";
}
